"use client";

import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { parseJsonBody, generateHtmlFromEditorContent } from "@/lib/general.utils";
import { getAboutEditorContent } from "@/lib/about.utils";
import useGeneralStore from "@/store/General/useGeneralStore";
import { useTranslation } from "next-i18next";

const AboutComponent = () => {
  const { i18n } = useTranslation("common");
  const language = i18n.language;
  const { AboutComponent: aboutData, isLoadingAboutComponent } = useGeneralStore((state) => ({
    AboutComponent: state.AboutComponent,
    isLoadingAboutComponent: state.isLoadingAboutComponent,
  }));
  const jsonBody = aboutData?.jsonBody;
  const aboutJson = useMemo(() => parseJsonBody(jsonBody), [jsonBody]);
  const [convertedContent, setConvertedContent] = useState<{ source: string | undefined; language: string; html: string } | null>(null);
  const hasSavedHtml = typeof aboutJson?.html === "string";

  useEffect(() => {
    let cancelled = false;
    // Older records are converted from their fetched fields, never from static copy.
    if (!hasSavedHtml && aboutJson) {
      void generateHtmlFromEditorContent(getAboutEditorContent(aboutJson, language))
        .then((html) => {
          if (!cancelled) setConvertedContent({ source: jsonBody, language, html });
        });
    }
    return () => { cancelled = true; };
  }, [aboutJson, hasSavedHtml, jsonBody, language]);

  const html = hasSavedHtml
    ? aboutJson.html
    : convertedContent?.source === jsonBody && convertedContent?.language === language
      ? convertedContent.html
      : "";
  const loading = isLoadingAboutComponent || Boolean(aboutJson && !hasSavedHtml && convertedContent?.source !== jsonBody);

  if (!loading && !html.trim()) return null;

  return (
    <section className="relative isolate overflow-hidden bg-[#f6f8f3] text-primary" dir={language === "ar" ? "rtl" : "ltr"}>
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -end-40 h-[32rem] w-[32rem] rounded-full bg-secondary/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -start-32 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 py-12 md:px-10 md:py-20 grid items-start gap-10 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-14">
        <div aria-hidden="true" className="pointer-events-none mx-auto w-44 lg:w-full lg:sticky lg:top-28">
          <svg viewBox="0 0 260 320" fill="none" className="w-full" focusable="false">
            <circle cx="130" cy="135" r="102" fill="white" />
            <circle cx="130" cy="135" r="115" stroke="#0b304a" strokeOpacity="0.1" strokeDasharray="3 10" />
            <path d="M130 181V112" stroke="#0b304a" strokeWidth="5" strokeLinecap="round" />
            <path d="M129 142C90 142 69 115 72 89C108 88 132 108 129 142Z" fill="#8cc63e" />
            <path d="M132 120C130 86 153 66 185 67C187 98 164 121 132 120Z" fill="#0b304a" />
            <path d="M89 107L130 144M168 84L132 121" stroke="white" strokeOpacity="0.65" strokeWidth="2" strokeLinecap="round" />
            <path d="M56 176L83 198C90 204 99 207 108 207H145C157 207 164 201 164 192C164 184 157 179 147 179H119L97 160C88 152 74 152 64 160" stroke="#0b304a" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M164 193L195 170C205 162 215 162 221 169C227 176 224 184 216 191L168 231C160 238 150 242 139 242H100L70 221" stroke="#0b304a" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M40 171L61 155L92 218L72 233L40 171Z" fill="#0b304a" />
            <circle cx="57" cy="176" r="3" fill="#8cc63e" />
            <circle cx="43" cy="65" r="7" fill="#8cc63e" />
            <circle cx="222" cy="129" r="5" fill="#8cc63e" />
            <path d="M209 47V61M202 54H216" stroke="#8cc63e" strokeWidth="3" strokeLinecap="round" />
            <path d="M84 282H176" stroke="#0b304a" strokeOpacity="0.12" strokeWidth="2" strokeLinecap="round" />
            <circle cx="119" cy="282" r="5" fill="#8cc63e" />
            <circle cx="140" cy="282" r="5" fill="#0b304a" />
          </svg>
        </div>
        <div className="relative min-w-0 overflow-hidden rounded-[1.75rem] border border-primary/10 bg-white shadow-[0_16px_60px_-24px_rgba(11,48,74,0.18)]">
          <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-primary via-secondary to-secondary/40" />
          {loading ? (
            <div className="p-8 md:p-12" aria-busy="true">
              <Skeleton width="35%" height={48} />
              {[1, 2, 3, 4, 5].map((line) => <Skeleton key={line} height={32} />)}
            </div>
          ) : (
            <article className="about-content p-7 md:p-12" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    </section>
  );
};

export default AboutComponent;
